 import { Header } from "@/components/Header";
 import { Footer } from "@/components/Footer";
 import { SEO } from "@/components/SEO";
 import { Button } from "@/components/ui/button";
 import { Card, CardContent } from "@/components/ui/card";
 import { Clock, Brain, Zap, Coffee, ArrowRight, Timer, Target, BarChart3 } from "lucide-react";
 import { Link } from "react-router-dom";
 
 const features = [
   { icon: Timer, title: "25분 집중", description: "25분 동안 한 가지 작업에 집중하세요. 짧은 시간이 집중력을 높여줍니다." },
   { icon: Coffee, title: "5분 휴식", description: "25분 집중 후 5분 휴식. 뇌가 정보를 정리할 시간을 주세요." },
   { icon: Brain, title: "과학적 방법", description: "뇌 과학에 기반한 시간 관리 기법으로 생산성을 극대화하세요." },
   { icon: Zap, title: "번아웃 방지", description: "규칙적인 휴식으로 장시간 집중해도 지치지 않습니다." },
   { icon: Target, title: "목표 달성", description: "작은 목표를 반복적으로 달성하며 성취감을 느끼세요." },
   { icon: BarChart3, title: "진행 추적", description: "완료한 포모도로 수를 추적하고 생산성을 분석하세요." },
 ];
 
 export default function PomodoroTechniqueKr() {
   return (
     <div className="min-h-screen bg-background">
       <SEO 
         title="포모도로 기법 | CSW | Online Workspace | Productivity Tool | Study Stream"
         description="포모도로 기법으로 집중력과 생산성을 높이세요. 25분 집중, 5분 휴식의 과학적인 시간 관리 방법을 배우세요."
         keywords="포모도로 기법, 뽀모도로, 시간 관리, 집중력 향상, 생산성, 공부 방법"
         canonicalUrl="/kr/pomodoro-technique/"
       />
       <Header />
       
       <main>
         <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
           <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted" />
           <div className="absolute top-20 left-20 w-96 h-96 bg-primary/20 rounded-full blur-[120px] animate-glow-pulse" />
           
           <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8">
             <div className="max-w-4xl mx-auto text-center space-y-8">
               <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
                 <span className="text-gradient">포모도로 기법</span>
               </h1>
               <p className="text-xl sm:text-2xl text-muted-foreground font-light max-w-xl mx-auto">
                 25분 집중, 5분 휴식. 과학적인 시간 관리로 생산성을 극대화하세요.
               </p>
               <Button size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8 py-6 text-lg" asChild>
                 <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                   포모도로 시작하기 <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                 </a>
               </Button>
               
               <div className="flex items-center justify-center gap-8 pt-8">
                 <div>
                   <p className="text-3xl font-bold text-gradient">25분</p>
                   <p className="text-sm text-muted-foreground">집중</p>
                 </div>
                 <div className="w-px h-12 bg-border" />
                 <div>
                   <p className="text-3xl font-bold text-gradient">5분</p>
                   <p className="text-sm text-muted-foreground">휴식</p>
                 </div>
                 <div className="w-px h-12 bg-border" />
                 <div>
                   <p className="text-3xl font-bold text-gradient">4회</p>
                   <p className="text-sm text-muted-foreground">긴 휴식</p>
                 </div>
               </div>
             </div>
           </div>
         </section>
 
         <section className="py-24 px-4 relative overflow-hidden">
           <div className="container mx-auto max-w-6xl">
             <div className="text-center mb-16">
               <h2 className="text-4xl md:text-5xl font-bold font-display mb-6">
                 왜 <span className="text-gradient">포모도로</span>인가요?
               </h2>
             </div>
             <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
               {features.map((feature) => {
                 const Icon = feature.icon;
                 return (
                   <Card key={feature.title} className="group bg-card/50 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-all hover:scale-105">
                     <CardContent className="p-8 space-y-4">
                       <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                         <Icon className="w-6 h-6 text-primary" />
                       </div>
                       <h3 className="text-xl font-semibold text-foreground font-display">{feature.title}</h3>
                       <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                     </CardContent>
                   </Card>
                 );
               })}
             </div>
           </div>
         </section>
 
         <section className="py-24 px-4 relative overflow-hidden">
           <div className="max-w-4xl mx-auto text-center">
             <h2 className="text-4xl md:text-5xl font-bold mb-6 font-display">
               지금 <span className="text-gradient">집중</span>을 시작하세요
             </h2>
             <Button size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-10 py-7 text-lg" asChild>
               <a href="https://app.csw.live" target="_blank" rel="noopener noreferrer">
                 무료로 시작하기 <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
               </a>
             </Button>
           </div>
         </section>
       </main>
       
       <Footer />
     </div>
   );
 }